import { jsx as _jsx } from "react/jsx-runtime";
import { EntryFlow } from "healthex-common/heap.dtos";
import { computeProjectTypeLabel } from "healthex-common/heap.utils";
import { ProjectType } from "healthex-common/study.dtos";
import { reaction } from "mobx";
import * as React from "react";
import { RouterProvider } from "react-router";
import "reflect-metadata";
import { getHeapGlobalEventProperties, updateHeapGlobalEventProperties } from "./Infra/LoadHeap";
import { NOT_LOADED } from "./Infra/RemoteData";
import { NamedRoutes } from "./UI/NamedRoutes";
import { buildRouteUrlWithQuery } from "./UI/navigationUrl";
import { createNamedRouter } from "./UI/ReactNamedRouter";
export const AppRouter = (props) => {
    var _a;
    const router = createNamedRouter(props.routerFactory, props.routingPaths(props.container));
    const initialMatch = router.state.matches.length ? router.state.matches[router.state.matches.length - 1] : null;
    const initialRoute = initialMatch && initialMatch.route;
    const initialNamedRoute = initialRoute ? router.getResolvedRoute(initialRoute) : null;
    const initialRouteParams = (_a = initialMatch === null || initialMatch === void 0 ? void 0 : initialMatch.params) !== null && _a !== void 0 ? _a : null;
    setDocumentTitle(initialRoute, initialRouteParams);
    updateHeapProjectId();
    updateHeapIASProperty();
    updateHeapIsDataUpdateProperty();
    updateHeapEntryFlowProperty();
    props.container.routingUIManager.initialize(initialNamedRoute, initialRouteParams, (currentRoute, currentRouteParams, isRedirect, queryParams = {}) => {
        const url = router.urlFromNamedRoute(currentRoute);
        if (url) {
            props.container.invalidationManager.invalidate();
            const urlWithParams = buildRouteUrlWithQuery(url, currentRouteParams, queryParams);
            router.navigate(urlWithParams, { replace: isRedirect });
            const { route, routeParams } = getCurrentRouteInfo();
            setDocumentTitle(route, routeParams);
            updateHeapProjectId();
            updateHeapIASProperty();
            updateHeapIsDataUpdateProperty();
            updateHeapEntryFlowProperty();
            window.scrollTo(0, 0);
        }
        else {
            console.log("WARNING: Attempted to navigate to an unbound route. Add this route to the configuration!");
        }
    });
    React.useEffect(() => {
        const onHashChange = () => {
            const { route, namedRoute, routeParams } = getCurrentRouteInfo();
            setDocumentTitle(route, routeParams);
            updateHeapProjectId();
            updateHeapIASProperty();
            updateHeapIsDataUpdateProperty();
            updateHeapEntryFlowProperty();
            void props.container.routingUIManager.userNavigatedTo(namedRoute, routeParams !== null && routeParams !== void 0 ? routeParams : {});
        };
        window.addEventListener("hashchange", onHashChange);
        return () => window.removeEventListener("hashchange", onHashChange);
    }, [props.container]);
    reaction(() => props.container.studiesUIManager.studyToManage, () => {
        updateHeapProjectId();
        updateHeapEntryFlowProperty();
    }, { fireImmediately: true });
    reaction(() => props.container.addProjectUIManager.study, () => {
        updateHeapProjectId();
        updateHeapEntryFlowProperty();
    }, { fireImmediately: true });
    reaction(() => props.container.patientConsentUIManager.session.studyPublicInfo, () => {
        updateHeapProjectId();
        updateHeapEntryFlowProperty();
    }, { fireImmediately: true });
    reaction(() => props.container.patientConsentUIManager.isIAS, () => updateHeapIASProperty(), { fireImmediately: true });
    reaction(() => props.container.patientConsentUIManager.isDataUpdate, () => updateHeapIsDataUpdateProperty(), { fireImmediately: true });
    reaction(() => [props.container.patientConsentUIManager.isIAS, props.container.patientConsentUIManager.isDataUpdate], () => updateHeapEntryFlowProperty(), { fireImmediately: true });
    return (_jsx(React.StrictMode, { children: _jsx(RouterProvider, { router: router, useTransitions: false }) }));
    function getCurrentRouteInfo() {
        var _a;
        const match = router.state.matches.length ? router.state.matches[router.state.matches.length - 1] : null;
        const route = match && match.route;
        const namedRoute = route ? router.getResolvedRoute(route) : null;
        const routeParams = (_a = match === null || match === void 0 ? void 0 : match.params) !== null && _a !== void 0 ? _a : null;
        return { route, namedRoute, routeParams };
    }
    function setDocumentTitle(route, routeParams) {
        const defaultTitle = "HealthEx";
        if (!(route === null || route === void 0 ? void 0 : route.title)) {
            document.title = defaultTitle;
        }
        else {
            if (typeof route.title === "function") {
                if (routeParams) {
                    document.title = `${defaultTitle} | ${route.title(routeParams)}`;
                }
                else {
                    document.title = defaultTitle;
                }
            }
            else {
                document.title = `${defaultTitle} | ${route.title}`;
            }
        }
    }
    function updateHeapProjectId() {
        try {
            const projectId = resolveCurrentProjectId();
            if (!projectId) {
                heap.removeEventProperty("projectId");
                heap.removeEventProperty("projectType");
                updateHeapGlobalEventProperties({ projectId: null, projectType: null });
                return;
            }
            heap.addEventProperties({ projectId });
            try {
                const matchedStudy = findMatchedStudy(projectId);
                let projectTypeLabel = matchedStudy ? computeProjectTypeLabel(matchedStudy.projectType, matchedStudy.pdda) : null;
                if (!projectTypeLabel && isAddProjectRoute()) {
                    const formProjectType = props.container.addProjectUIManager.studyFormModel.input.projectType;
                    const isPddeProvisional = props.container.authManager.userType === "EXTERNAL_RESEARCHER";
                    projectTypeLabel = computeProjectTypeLabel(formProjectType, isPddeProvisional);
                }
                if (projectTypeLabel) {
                    heap.addEventProperties({ projectType: projectTypeLabel });
                }
                else {
                    heap.removeEventProperty("projectType");
                }
                updateHeapGlobalEventProperties({ projectId, projectType: projectTypeLabel !== null && projectTypeLabel !== void 0 ? projectTypeLabel : null });
            }
            catch (error) {
                console.error("Error setting Heap event properties", error);
            }
        }
        catch (error) {
            console.error("Error setting Heap projectId", error);
        }
    }
    function updateHeapIASProperty() {
        try {
            const isIAS = props.container.patientConsentUIManager.isIAS;
            if (isIAS) {
                heap.addEventProperties({ isIAS: true });
            }
            else {
                heap.removeEventProperty("isIAS");
            }
            updateHeapGlobalEventProperties({ isIAS: isIAS ? true : null });
        }
        catch (error) {
            console.error("Error setting Heap isIAS property", error);
        }
    }
    function updateHeapIsDataUpdateProperty() {
        try {
            const isDataUpdate = props.container.patientConsentUIManager.isDataUpdate;
            if (isDataUpdate) {
                heap.addEventProperties({ isDataUpdate: true });
            }
            else {
                heap.removeEventProperty("isDataUpdate");
            }
            updateHeapGlobalEventProperties({ isDataUpdate: isDataUpdate ? true : null });
        }
        catch (error) {
            console.error("Error setting Heap isDataUpdate property", error);
        }
    }
    function updateHeapEntryFlowProperty() {
        try {
            const isIAS = props.container.patientConsentUIManager.isIAS;
            const isDataUpdate = props.container.patientConsentUIManager.isDataUpdate;
            const projectType = resolveCurrentProjectType();
            const entryFlow = deriveEntryFlow({ isIAS, isDataUpdate, projectType });
            if (entryFlow !== EntryFlow.DIRECT) {
                heap.addEventProperties({ entryFlow });
                updateHeapGlobalEventProperties({ entryFlow });
            }
            else if (!getHeapGlobalEventProperties().entryFlow) {
                heap.addEventProperties({ entryFlow: EntryFlow.DIRECT });
                updateHeapGlobalEventProperties({ entryFlow: EntryFlow.DIRECT });
            }
        }
        catch (error) {
            console.error("Error setting Heap entryFlow property", error);
        }
    }
    function resolveCurrentProjectType() {
        var _a;
        const projectId = resolveCurrentProjectId();
        if (!projectId)
            return null;
        const matchedStudy = findMatchedStudy(projectId);
        if (matchedStudy === null || matchedStudy === void 0 ? void 0 : matchedStudy.projectType)
            return matchedStudy.projectType;
        if (isAddProjectRoute()) {
            return (_a = props.container.addProjectUIManager.studyFormModel.input.projectType) !== null && _a !== void 0 ? _a : null;
        }
        return null;
    }
    function resolveCurrentProjectId() {
        var _a, _b, _c;
        const matchWithId = router.state.matches
            .slice()
            .reverse()
            .find((m) => { var _a, _b, _c; return (_b = (_a = m === null || m === void 0 ? void 0 : m.params) === null || _a === void 0 ? void 0 : _a.projectId) !== null && _b !== void 0 ? _b : (_c = m === null || m === void 0 ? void 0 : m.params) === null || _c === void 0 ? void 0 : _c.studyId; });
        return (_b = (_a = matchWithId === null || matchWithId === void 0 ? void 0 : matchWithId.params) === null || _a === void 0 ? void 0 : _a.projectId) !== null && _b !== void 0 ? _b : (_c = matchWithId === null || matchWithId === void 0 ? void 0 : matchWithId.params) === null || _c === void 0 ? void 0 : _c.studyId;
    }
    function findMatchedStudy(projectId) {
        var _a;
        const sources = [
            props.container.studiesUIManager.studyToManage,
            props.container.addProjectUIManager.study,
            props.container.patientConsentUIManager.session.studyPublicInfo,
        ];
        const isMatch = (source) => source !== NOT_LOADED && source !== null && source.id === projectId;
        return (_a = sources.find(isMatch)) !== null && _a !== void 0 ? _a : null;
    }
    function isAddProjectRoute() {
        var _a;
        return ((_a = props.container.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.PROJECTS_ADD_PROJECT.routeName;
    }
};
export function deriveEntryFlow(signals) {
    if (signals.projectType === ProjectType.AI_DATA_USE)
        return EntryFlow.AI;
    if (signals.isDataUpdate)
        return EntryFlow.GDU;
    if (signals.isIAS)
        return EntryFlow.IAS;
    return EntryFlow.DIRECT;
}
